/**
 * QC Order Detail Page
 * Shows QC order details, status history, and allows state machine transitions.
 * Handles version field for optimistic locking.
 * 
 * State machine: pending → in_progress → passed / failed / retest_required → (terminal or retest)
 */
import { useEffect, useState } from 'react';
import { useParams } from 'wouter';
import { useQcOrderDetail, useQcOrderTransition, type QcOrderView } from '@/hooks/useLab';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { canReadQcOrder, canUpdateQcOrder } from '@/lib/labPermissions';

type QcOrderStatus = 'pending' | 'in_progress' | 'passed' | 'failed' | 'retest_required' | 'cancelled';

// Valid state transitions per state-machines.md
// QC Orders: pending → sampling → testing → reviewing → passed / failed
const VALID_TRANSITIONS: Record<string, string[]> = {
  pending: ['in_progress', 'cancelled'],
  in_progress: ['passed', 'failed', 'retest_required', 'cancelled'],
  passed: ['cancelled'],
  failed: ['cancelled'],
  retest_required: ['in_progress', 'cancelled'],
  cancelled: [],
};

export default function QcOrderDetailPage() {
  const { qcOrderId } = useParams<{ qcOrderId: string }>();
  const { permissions } = useAuth();
  const { fetch: fetchDetail, loading: detailLoading } = useQcOrderDetail(qcOrderId!);
  const { transition: transitionStatus, loading: transitionLoading } = useQcOrderTransition(qcOrderId!);

  const [order, setOrder] = useState<QcOrderView | null>(null);
  const [selectedTransition, setSelectedTransition] = useState<QcOrderStatus | ''>('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Check permission — wildcard-aware (mirrors backend RBAC)
  if (!canReadQcOrder(permissions)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="p-6 text-center">
          <h2 className="text-lg font-semibold">权限不足</h2>
          <p className="text-sm text-muted-foreground">您没有查看质检单的权限</p>
        </Card>
      </div>
    );
  }

  // Load QC order detail
  useEffect(() => {
    const loadOrder = async () => {
      try {
        const data = await fetchDetail();
        if (data) {
          setOrder(data);
        }
      } catch (err) {
        toast.error('加载质检单详情失败');
      }
    };

    if (qcOrderId) {
      loadOrder();
    }
  }, [qcOrderId, fetchDetail]);

  const handleTransition = async () => {
    if (!order || !selectedTransition) {
      toast.error('请选择转换状态');
      return;
    }

    if (!canUpdateQcOrder(permissions)) {
      toast.error('您没有修改质检单状态的权限');
      return;
    }

    try {
      await transitionStatus(selectedTransition, order.version);
      toast.success('状态已更新');
      setSelectedTransition('');
      setConfirmDialogOpen(false);
      
      // Refetch order details to ensure consistency with backend
      const updatedData = await fetchDetail();
      if (updatedData) {
        setOrder(updatedData);
      }
    } catch (err: unknown) {
      // Backend rejection must be shown as-is
      toast.error(`状态转换失败: ${(err as Error)?.message || '未知错误'}`);
    }
  };

  if (detailLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-sm text-muted-foreground">加载中…</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="p-6 text-center">
          <h2 className="text-lg font-semibold">质检单不存在</h2>
        </Card>
      </div>
    );
  }

  const validTransitions = VALID_TRANSITIONS[order.status] || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">质检单详情</h1>
        <p className="text-sm text-muted-foreground">质检单 ID: {order.id}</p>
      </div>

      {/* QC Order Info */}
      <Card className="p-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-semibold text-muted-foreground">批次 ID</label>
            <p className="mt-1 text-sm font-mono">{order.cellBatchId}</p>
          </div>
          <div>
            <label className="text-sm font-semibold text-muted-foreground">当前状态</label>
            <p className="mt-1 text-sm">
              <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${getStatusColor(order.status)}`}>
                {getStatusLabel(order.status)}
              </span>
            </p>
          </div>
          <div>
            <label className="text-sm font-semibold text-muted-foreground">版本号</label>
            <p className="mt-1 text-sm font-mono">{order.version}</p>
          </div>
          <div>
            <label className="text-sm font-semibold text-muted-foreground">创建时间</label>
            <p className="mt-1 text-sm">{new Date(order.createdAt).toLocaleString()}</p>
          </div>

        </div>
      </Card>

      {/* Status Transition */}
      {validTransitions.length > 0 && canUpdateQcOrder(permissions) && (
        <Card className="p-6">
          <h3 className="mb-4 text-lg font-semibold">状态转换</h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold">选择目标状态</label>
              <select
                value={selectedTransition}
                onChange={(e) => setSelectedTransition((e.target.value || '') as QcOrderStatus | '')}
                className="mt-2 w-full rounded border border-input bg-background px-3 py-2 text-sm"
                disabled={transitionLoading}
              >
                <option value="">-- 选择状态 --</option>
                {validTransitions.map((status) => (
                  <option key={status} value={status}>
                    {getStatusLabel(status)}
                  </option>
                ))}
              </select>
            </div>
            <Button
              onClick={() => setConfirmDialogOpen(true)}
              disabled={!selectedTransition || transitionLoading}
              className="w-full"
            >
              {transitionLoading ? '处理中…' : '确认转换'}
            </Button>
          </div>
        </Card>
      )}

      {/* Confirmation Dialog (simple version) */}
      {confirmDialogOpen && (
        <Card className="border-yellow-200 bg-yellow-50 p-6">
          <h4 className="font-semibold text-yellow-900">确认状态转换</h4>
          <p className="mt-2 text-sm text-yellow-800">
            您确定要将质检单从 <strong>{getStatusLabel(order.status)}</strong> 转换到{' '}
            <strong>{getStatusLabel(selectedTransition)}</strong> 吗？
          </p>
          <div className="mt-4 flex gap-2">
            <Button
              onClick={handleTransition}
              disabled={transitionLoading}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              {transitionLoading ? '处理中…' : '确认'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmDialogOpen(false);
                setSelectedTransition('');
              }}
              disabled={transitionLoading}
            >
              取消
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: '待处理',
    in_progress: '进行中',
    passed: '已通过',
    failed: '已失败',
    retest_required: '需重检',
    cancelled: '已取消',
  };
  return labels[status] || status;
}

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-900',
    in_progress: 'bg-blue-100 text-blue-900',
    passed: 'bg-green-100 text-green-900',
    failed: 'bg-red-100 text-red-900',
    retest_required: 'bg-yellow-100 text-yellow-900',
    cancelled: 'bg-gray-100 text-gray-900',
  };
  return colors[status] || 'bg-gray-100 text-gray-900';
}
